import { Component, OnInit, TemplateRef, ViewChild, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { AdminService } from '../services/admin/admin.service';
import { HelpersService } from '../../shared/servicio/helpers/helpers.service';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-pop',
  templateUrl: './pop.component.html',
  styleUrls: ['./pop.component.css']
})
export class PopComponent implements OnInit, OnDestroy {

  @ViewChild('createTemplate') createTemplate!: TemplateRef<any>;
  @ViewChild('editTemplate') editTemplate!: TemplateRef<any>;
  @ViewChild('deleteTemplate') deleteTemplate!: TemplateRef<any>;

  // Subject para manejar la destrucción del componente
  private destroy$ = new Subject<void>();

  // Modal references
  modalRef: BsModalRef;
  modalConfig = {
    class: 'modal-dialog-centered modal-app modal-app-lg',
    backdrop: true,
    ignoreBackdropClick: true
  };

  // Controles de formulario - SOLO los necesarios
  targetUsersFC: FormControl;

  // Formularios
  popupForm: FormGroup;
  submitted: boolean = false;
  preloader: boolean = false;
  div_error: number = 0;
  div_text_error: string = '';

  // Lista de popups
  list_popups: any[] = [];

  // Paginación
  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;
  totalItems: number = 0;

  // Filtros simplificados
  activeFilter: boolean | undefined = true; // Por defecto solo activos
  targetUsersFilter: string = '';

  // Estados
  showLoading: boolean = false;
  isRefreshing: boolean = false;

  // Para editar/eliminar
  selectedPopup: any = null;
  isEditMode: boolean = false;
  imageChanged: boolean = false;

  // Propiedades para manejo de imagen
  selectedImageFile: File | null = null;
  imagePreview: string | ArrayBuffer | null = null;
  isUploadingImage: boolean = false;
  imageError: string = '';
  maxImageSize: number = 5 * 1024 * 1024; // 5MB
  allowedImageTypes: string[] = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

  // Opciones para formularios
  popupTypes = [
    { value: 'advertisement', label: 'Publicidad' },
    { value: 'announcement', label: 'Anuncio' },
    { value: 'alert', label: 'Alerta' },
    { value: 'promotion', label: 'Promoción' },
    { value: 'educational', label: 'Educativo' }
  ];

  frequencies = [
    { value: 'once', label: 'Una vez por usuario' },
    { value: 'daily', label: 'Una vez al día' },
    { value: 'weekly', label: 'Una vez a la semana' },
    { value: 'monthly', label: 'Una vez al mes' },
    { value: 'always', label: 'Siempre al ingresar' }
  ];

  targetUsersOptions = [
    { value: '', label: 'Todos los usuarios' },
    { value: 'all', label: 'Todos los usuarios' },
    { value: 'medics', label: 'Solo médicos' },
    { value: 'secretaries', label: 'Solo secretarias' },
    { value: 'admins', label: 'Solo administradores' }
  ];

  constructor(
    private popupService: AdminService,
    public helper: HelpersService,
    private modalService: BsModalService,
    private formBuilder: FormBuilder
  ) { }

  ngOnInit() {
    console.log('PopComponent initialized');
    this.initializeFormControls();
    this.initializeForm();
    this.loadPopups();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeFormControls() {
    this.targetUsersFC = new FormControl('');

    // Solo suscribirse al cambio del filtro de usuarios
    this.targetUsersFC.valueChanges.subscribe((value) => {
      this.targetUsersFilter = value || '';
      this.currentPage = 1;
      this.loadPopups();
    });
  }

  // Método para alternar filtro de estado
  toggleStatusFilter() {
    if (this.activeFilter === true) {
      this.activeFilter = false; // Cambiar a solo inactivos
    } else if (this.activeFilter === false) {
      this.activeFilter = undefined; // Cambiar a todos
    } else {
      this.activeFilter = true; // Cambiar a solo activos
    }

    this.currentPage = 1;
    this.loadPopups();
  }

  // Método para obtener el texto del estado actual del filtro
  getStatusFilterText(): string {
    if (this.activeFilter === true) {
      return 'SOLO ACTIVOS';
    } else if (this.activeFilter === false) {
      return 'SOLO INACTIVOS';
    } else {
      return 'TODOS';
    }
  }

  // Método para obtener el ícono del estado actual del filtro
  getStatusFilterIcon(): string {
    if (this.activeFilter === true) {
      return 'assets/img/icons/icon-order-doctor-activated.png';
    } else if (this.activeFilter === false) {
      return 'assets/img/icons/icon-order-doctor-deactivated.png';
    } else {
      return 'assets/img/icons/icon-filter.png';
    }
  }

  // Método para obtener la clase CSS del botón según el estado
  getStatusFilterClass(): string {
    if (this.activeFilter === true) {
      return 'bg-success text-white';
    } else if (this.activeFilter === false) {
      return 'bg-danger text-white';
    } else {
      return 'bg-white text-mefid-purple1';
    }
  }

  // Método para limpiar filtros
  clearFilters() {
    this.targetUsersFC.setValue('');
    this.targetUsersFilter = '';
    this.activeFilter = true; // Volver al estado por defecto (solo activos)
    this.currentPage = 1;
    this.loadPopups();
  }

  // Método para obtener el texto descriptivo del filtro actual
  getFilterDescription(): string {
    let description = '';

    if (this.activeFilter === true) {
      description += 'Solo popups activos';
    } else if (this.activeFilter === false) {
      description += 'Solo popups inactivos';
    } else {
      description += 'Todos los popups';
    }

    if (this.targetUsersFilter) {
      const userType = this.getTargetUsersText(this.targetUsersFilter);
      description += ` - Usuarios: ${userType}`;
    }

    return description;
  }

  initializeForm() {
    this.popupForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      title: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(200)]],
      content: ['', [Validators.required, Validators.minLength(5)]],
      image: [''],
      start_date: ['', [Validators.required]],
      end_date: ['', [Validators.required]],
      popup_type: ['advertisement', [Validators.required]],
      frequency: ['once', [Validators.required]],
      target_users: ['all', [Validators.required]],
      is_modal: [true],
      auto_close: [true],
      auto_close_seconds: [null],
      primary_button_text: ['OK', [Validators.required]],
      primary_button_url: ['', [this.urlValidator]],
      secondary_button_text: [''],
      secondary_button_url: ['', [this.urlValidator]],
      active: [true],
      priority: [1, [Validators.required, Validators.min(1), Validators.max(10)]]
    }, { validators: this.dateValidator });
  }

  // === MÉTODOS DE MANEJO DE IMAGEN ===

  private validateImageFile(file: File): string | null {
    if (!this.allowedImageTypes.includes(file.type)) {
      return 'Solo se permiten archivos JPG, PNG y GIF';
    }

    if (file.size > this.maxImageSize) {
      return `El archivo debe ser menor a ${this.formatFileSize(this.maxImageSize)}`;
    }

    if (file.name.length > 100) {
      return 'El nombre del archivo es demasiado largo';
    }

    return null;
  }

  onImageSelected(event: any) {
    const file = event.target.files[0];

    if (!file) {
      this.clearImage();
      return;
    }

    const validationError = this.validateImageFile(file);
    if (validationError) {
      this.imageError = validationError;
      this.clearImage();
      return;
    }

    this.selectedImageFile = file;
    this.imageError = '';
    this.imageChanged = true;

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = (e.target as FileReader).result;
      this.imagePreview = result || null;
    };
    reader.onerror = () => {
      this.imageError = 'Error al leer el archivo';
      this.clearImage();
    };
    reader.readAsDataURL(file);
  }

  private convertImageToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = (e.target as FileReader).result as string;
        resolve(result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  clearImage() {
    this.selectedImageFile = null;
    this.imagePreview = null;
    this.imageError = '';
    this.imageChanged = true;
    this.popupForm.patchValue({ image: '' });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Métodos para drag & drop
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const element = event.currentTarget as HTMLElement;
    element.classList.add('dragover');
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const element = event.currentTarget as HTMLElement;
    element.classList.remove('dragover');
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const element = event.currentTarget as HTMLElement;
    element.classList.remove('dragover');

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const simulatedEvent = {
        target: {
          files: [file]
        }
      };
      this.onImageSelected(simulatedEvent);
    }
  }

  // === VALIDADORES ===

  urlValidator(control: FormControl) {
    if (!control.value || control.value.trim() === '') {
      return null;
    }

    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    const value = control.value.trim();

    if (!value.startsWith('http://') && !value.startsWith('https://')) {
      const correctedValue = 'https://' + value;
      if (urlPattern.test(correctedValue)) {
        control.setValue(correctedValue);
        return null;
      }
    }

    return urlPattern.test(value) ? null : { invalidUrl: true };
  }

  dateValidator(group: FormGroup) {
    const startDate = group.get('start_date').value;
    const endDate = group.get('end_date').value;

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start >= end) {
        return { dateInvalid: true };
      }
    }
    return null;
  }

  get f() {
    return this.popupForm.controls;
  }

  // === CARGA DE DATOS OPTIMIZADA ===

  loadPopups() {
    if (this.showLoading || this.isRefreshing) {
      return;
    }

    this.showLoading = true;

    this.popupService.listPopups(
      this.currentPage,
      this.pageSize,
      this.activeFilter,
      this.targetUsersFilter,
      '' // searchFilter vacío ya que lo quitamos
    ).subscribe({
      next: (response: any) => {
        console.log('Popups response:', response);

        if (Array.isArray(response)) {
          this.list_popups = response;
          this.totalItems = response.length;
          this.totalPages = 1;
        } else {
          this.list_popups = response.results || [];
          this.totalItems = response.count || 0;
          this.totalPages = Math.ceil(this.totalItems / this.pageSize);
        }

        this.list_popups = this.list_popups.map(popup => ({
          ...popup,
          date_created: this.formatDate(popup.date_created),
          date_modified: this.formatDate(popup.date_modified)
        }));

        this.showLoading = false;
        this.isRefreshing = false;
      },
      error: (error) => {
        console.error('Error loading popups:', error);
        this.helper.error('Error al cargar popups', error);
        this.showLoading = false;
        this.isRefreshing = false;
      }
    });
  }

  private refreshPopupsAfterAction() {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;

    setTimeout(() => {
      this.loadPopups();
    }, 200);
  }

  // === MODAL METHODS ===

  openCreateModal() {
    this.isEditMode = false;
    this.selectedPopup = null;
    this.resetForm();
    this.modalRef = this.modalService.show(this.createTemplate, this.modalConfig);
  }

  openEditModal(popup: any) {
    this.isEditMode = true;
    this.selectedPopup = popup;
    this.loadPopupData(popup);
    this.modalRef = this.modalService.show(this.editTemplate, this.modalConfig);
  }

  openDeleteModal(popup: any) {
    this.selectedPopup = popup;
    this.modalRef = this.modalService.show(this.deleteTemplate, this.modalConfig);
  }

  closeModal() {
    this.modalRef.hide();
    this.resetForm();
  }

  resetForm() {
    this.popupForm.reset();
    this.initializeForm();
    this.submitted = false;
    this.preloader = false;
    this.div_error = 0;
    this.div_text_error = '';
    this.imageChanged = false;
    this.clearImage();
  }

  deletePopup() {
    this.preloader = true;

    this.popupService.deletePopup(this.selectedPopup.id).subscribe({
      next: () => {
        this.preloader = false;
        this.helper.modalSuccess('¡Popup eliminado exitosamente!');
        this.closeModal();
        this.refreshPopupsAfterAction();
      },
      error: (error) => {
        console.error('Error deleting popup:', error);
        this.preloader = false;
        this.helper.error('Error al eliminar popup', error);
      }
    });
  }

  togglePopupStatus(popup: any) {
    if (!popup || !popup.id) {
      console.error('Popup inválido:', popup);
      return;
    }

    if (popup.isToggling) {
      return;
    }

    popup.isToggling = true;
    const newStatus = !popup.active;

    this.popupService.togglePopupStatus(popup.id, newStatus).subscribe({
      next: (response) => {
        console.log('Toggle response:', response);

        popup.active = newStatus;
        popup.isToggling = false;

        this.helper.modalSuccess(
          `Popup ${newStatus ? 'activado' : 'desactivado'} exitosamente`
        );

        this.refreshPopupsAfterAction();
      },
      error: (error) => {
        console.error('Error toggling popup status:', error);
        popup.isToggling = false;

        const errorMessage = error.error.message || 'Error al cambiar estado del popup';
        this.helper.error(errorMessage, error);
      }
    });
  }

  // === UTILITY METHODS ===

  formatDate(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };

    return date.toLocaleDateString('es-ES', options);
  }

  formatDateForInput(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadPopups();
  }

  getPopupStatus(popup: any): string {
    if (!popup.active) {
      return 'Inactivo';
    }

    const now = new Date();
    const startDate = new Date(popup.start_date);
    const endDate = new Date(popup.end_date);

    if (now < startDate) {
      return 'Programado';
    } else if (now > endDate) {
      return 'Expirado';
    } else {
      return 'Activo';
    }
  }

  getStatusClass(popup: any): string {
    const status = this.getPopupStatus(popup);
    switch (status) {
      case 'Activo':
        return 'text-success';
      case 'Programado':
        return 'text-warning';
      case 'Expirado':
        return 'text-danger';
      case 'Inactivo':
        return 'text-muted';
      default:
        return 'text-muted';
    }
  }

  getPopupTypeText(type: string): string {
    const typeObj = this.popupTypes.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  }

  getFrequencyText(frequency: string): string {
    const freqObj = this.frequencies.find(f => f.value === frequency);
    return freqObj ? freqObj.label : frequency;
  }

  getTargetUsersText(targetUsers: string): string {
    const targetObj = this.targetUsersOptions.find(t => t.value === targetUsers);
    return targetObj ? targetObj.label : targetUsers;
  }

  async onSubmit() {
    this.submitted = true;
    this.div_error = 0;
    this.div_text_error = '';

    if (this.popupForm.errors && this.popupForm.errors['dateInvalid']) {
      this.div_error = 1;
      this.div_text_error = 'La fecha de inicio debe ser anterior a la fecha de fin';
      return;
    }

    if (this.popupForm.invalid) {
      this.div_error = 1;
      this.div_text_error = 'Por favor complete todos los campos requeridos correctamente';
      return;
    }

    this.preloader = true;

    try {
      const formData = this.popupForm.value;

      const popupData: any = {
        name: formData.name,
        title: formData.title,
        content: formData.content,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        popup_type: formData.popup_type,
        frequency: formData.frequency,
        target_users: formData.target_users,
        is_modal: formData.is_modal,
        auto_close: formData.auto_close,
        auto_close_seconds: formData.auto_close ? formData.auto_close_seconds : null,
        primary_button_text: formData.primary_button_text,
        primary_button_url: formData.primary_button_url || '',
        secondary_button_text: formData.secondary_button_text || '',
        secondary_button_url: formData.secondary_button_url || '',
        active: formData.active,
        priority: formData.priority
      };

      if (!this.isEditMode || this.imageChanged) {
        console.log('Procesando imagen (nueva o cambió)');

        if (this.selectedImageFile) {
          console.log('Convirtiendo nueva imagen a base64...');
          this.isUploadingImage = true;
          popupData.image = await this.convertImageToBase64(this.selectedImageFile);
          console.log('Nueva imagen convertida exitosamente');
          this.isUploadingImage = false;
        } else {
          console.log('No hay imagen - enviando campo vacío');
          popupData.image = '';
        }
      } else {
        console.log('Imagen no cambió - no se incluye en el payload');
      }

      console.log('Enviando datos del popup:', {
        ...popupData,
        image: popupData.image !== undefined ?
          (popupData.image ? `[BASE64_DATA - ${Math.round(popupData.image.length / 1024)}KB]` : 'Sin imagen')
          : 'Campo imagen no incluido'
      });

      const operation = this.isEditMode ?
        this.popupService.updatePopup(this.selectedPopup.id, popupData) :
        this.popupService.createPopup(popupData);

      operation.subscribe({
        next: (event) => {
          if (event.type === 4) {
            this.preloader = false;
            this.helper.modalSuccess(
              this.isEditMode ? '¡Popup actualizado exitosamente!' : '¡Popup creado exitosamente!'
            );
            this.closeModal();
            this.loadPopups()
          }
        },
        error: (error) => {
          console.error('Error with popup:', error);
          this.preloader = false;
          this.isUploadingImage = false;

          if (error.error && typeof error.error === 'object') {
            const backendErrors = error.error;
            let errorMessage = `Error al ${this.isEditMode ? 'actualizar' : 'crear'} el popup:<br>`;

            Object.keys(backendErrors).forEach(key => {
              if (Array.isArray(backendErrors[key])) {
                errorMessage += `• ${key}: ${backendErrors[key].join(', ')}<br>`;
              } else {
                errorMessage += `• ${key}: ${backendErrors[key]}<br>`;
              }
            });

            this.div_error = 1;
            this.div_text_error = errorMessage;
          } else {
            this.div_error = 1;
            this.div_text_error = `Error al ${this.isEditMode ? 'actualizar' : 'crear'} el popup`;
          }
        }
      });
    } catch (error) {
      console.error('Error processing popup:', error);
      this.preloader = false;
      this.isUploadingImage = false;
      this.div_error = 1;
      this.div_text_error = 'Error al procesar el popup';
    }
  }

  loadPopupData(popup: any) {
    console.log('Cargando datos del popup para edición:', popup);

    this.popupForm.patchValue({
      name: popup.name,
      title: popup.title,
      content: popup.content,
      image: popup.image || '',
      start_date: this.formatDateForInput(popup.start_date),
      end_date: this.formatDateForInput(popup.end_date),
      popup_type: popup.popup_type,
      frequency: popup.frequency,
      target_users: popup.target_users,
      is_modal: popup.is_modal,
      auto_close: popup.auto_close,
      auto_close_seconds: popup.auto_close_seconds,
      primary_button_text: popup.primary_button_text,
      primary_button_url: popup.primary_button_url || '',
      secondary_button_text: popup.secondary_button_text || '',
      secondary_button_url: popup.secondary_button_url || '',
      active: popup.active,
      priority: popup.priority
    });

    this.selectedImageFile = null;
    this.imageError = '';
    this.imageChanged = false;

    if (popup.image && popup.image.trim() !== '') {
      console.log('Mostrando imagen existente:', popup.image);
      this.imagePreview = popup.image;
    } else {
      console.log('Popup no tiene imagen');
      this.imagePreview = null;
    }
  }
}
